import React, { createRef, useEffect, useState } from "react";
import { Button, Col, Container, Navbar, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBed, faBullhorn, faChartColumn, faFolderOpen, faHouse, faUsers } from "@fortawesome/free-solid-svg-icons";
import useApp from "../../utils/useApp";
import SaveChangesModal from "./SaveChangesModal";
import { unsavedChanges } from "../../App";

interface MainHeaderMenuState {
  activeMyCases: boolean;
  activeCases: boolean;
  activeGroups: boolean;
  activeAnnouncements: boolean;
  activeReports: boolean;
  activeShelter: boolean;
  routeChange: any;
}

const MainHeaderMenu = ({
  activeMyCases,
  activeCases,
  activeGroups,
  activeAnnouncements,
  activeReports,
  activeShelter,
  routeChange,
}: MainHeaderMenuState) => {
  const { auth }: any = useApp();
  const [isSmediumScreen, setIsSmediumScreen] = useState(window.innerWidth < 1200);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 992);
  const myCaseBtnRef = createRef<HTMLButtonElement>();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [pendingRoute, setPendingRoute] = useState<string>("");
  const [accessMyCases, setAccessMyCases] = useState<boolean>(false);
  const [accessCases, setAccessCases] = useState<boolean>(false);
  const [accessGroups, setAccessGroups] = useState<boolean>(false);
  const [accessAnnouncements, setAccessAnnouncements] = useState<boolean>(false);
  const [accessReports, setAccessReports] = useState<boolean>(false);
  const [accessShelter, setAccessShelter] = useState<boolean>(false);

  useEffect(() => {
    setAccessPermissions();

    const handleResize = () => {
      setIsSmediumScreen(window.innerWidth < 1200);
      setIsSmallScreen(window.innerWidth < 992);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const setAccessPermissions = () => {
    switch (auth?.profile?.role) {
      case "org_admin":
        setAccessMyCases(true);
        setAccessCases(true);
        setAccessGroups(true);
        setAccessAnnouncements(true);
        setAccessReports(true);
        break;
      case "admin":
        setAccessMyCases(true);
        setAccessCases(true);
        setAccessGroups(true);
        setAccessAnnouncements(true);
        setAccessReports(true);
        break;
      case "res_support":
        setAccessMyCases(true);
        setAccessCases(true);
        break;
      default:
        setAccessMyCases(true);
        setAccessGroups(true);
        setAccessAnnouncements(true);
        break;
    }

    if (auth?.profile?.includeHMIS === 1) {
      setAccessShelter(true);
    }
  };

  const handleRouteChange = (route: string) => {
    if (unsavedChanges.value === true) {
      setShowModal(true);
      setPendingRoute(route);
    } else {
      routeChange(route);
    }
  };

  return (
    <>
      <Navbar className="nav-action z-3">
        <Container className="custom-container">
          <Row className="w-100 d-flex justify-content-center">
            {accessMyCases && (
              <Col className="custom-col d-flex justify-content-center">
                <Button
                  ref={myCaseBtnRef}
                  className={activeMyCases ? "button-nav app-tertiary-color" : "button-nav app-primary-color-inverse mx-0"}
                  onClick={() => handleRouteChange("/my-cases")}
                >
                  {!isSmallScreen ? (
                    <>
                      <FontAwesomeIcon icon={faHouse} className="me-2 fa-nav" />
                      My Cases
                    </>
                  ) : (
                    <FontAwesomeIcon icon={faHouse} />
                  )}
                </Button>
              </Col>
            )}
            {accessCases && (
              <Col className="custom-col d-flex justify-content-center">
                <Button
                  className={activeCases ? "button-nav app-tertiary-color" : "button-nav app-primary-color-inverse mx-0"}
                  onClick={() => handleRouteChange("/cases")}
                >
                  {!isSmallScreen ? (
                    <>
                      <FontAwesomeIcon icon={faFolderOpen} className="me-2 fa-nav" />
                      Cases
                    </>
                  ) : (
                    <FontAwesomeIcon icon={faFolderOpen} />
                  )}
                </Button>
              </Col>
            )}
            {accessGroups && (
              <Col className="custom-col d-flex justify-content-center">
                <Button
                  className={activeGroups ? "button-nav app-tertiary-color" : "button-nav app-primary-color-inverse mx-0"}
                  onClick={() => handleRouteChange("/groups")}
                >
                  {!isSmallScreen ? (
                    <>
                      <FontAwesomeIcon icon={faUsers} className="me-2 fa-nav" />
                      Groups
                    </>
                  ) : (
                    <FontAwesomeIcon icon={faUsers} />
                  )}
                </Button>
              </Col>
            )}
            {accessAnnouncements && (
              <Col className="custom-col d-flex justify-content-center">
                <Button
                  className={activeAnnouncements ? "button-nav app-tertiary-color" : "button-nav app-primary-color-inverse mx-0"}
                  onClick={() => handleRouteChange("/announcements")}
                >
                  {!isSmallScreen ? (
                    <>
                      <FontAwesomeIcon icon={faBullhorn} className="me-1 fa-nav" />
                      {isSmediumScreen ? "Announce" : "Announcement"}
                    </>
                  ) : (
                    <FontAwesomeIcon icon={faBullhorn} />
                  )}
                </Button>
              </Col>
            )}
            {accessShelter && (
              <Col className="custom-col d-flex justify-content-center">
                <Button
                  className={activeShelter ? "button-nav app-tertiary-color" : "button-nav app-primary-color-inverse mx-0"}
                  onClick={() => handleRouteChange("/shelter")}
                >
                  {!isSmallScreen ? (
                    <>
                      <FontAwesomeIcon icon={faBed} className="me-2 fa-nav" />
                      Shelter
                    </>
                  ) : (
                    <FontAwesomeIcon icon={faBed} />
                  )}
                </Button>
              </Col>
            )}
            {accessReports && (
              <Col className="custom-col d-flex justify-content-center">
                <Button
                  className={activeReports ? "button-nav app-tertiary-color" : "button-nav app-primary-color-inverse mx-0"}
                  onClick={() => handleRouteChange("/reports")}
                >
                  {!isSmallScreen ? (
                    <>
                      <FontAwesomeIcon icon={faChartColumn} className="me-2 fa-nav" />
                      Reports
                    </>
                  ) : (
                    <FontAwesomeIcon icon={faChartColumn} />
                  )}
                </Button>
              </Col>
            )}
          </Row>
        </Container>
      </Navbar>
      <SaveChangesModal showModal={showModal} setShowModal={setShowModal} path={pendingRoute} />
    </>
  );
};

export default MainHeaderMenu;
